import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { failsafes, failsafesResin } from '@shared/constants/failsafes';
import {
  printProfileStrings,
  printProfiles,
} from '@shared/constants/print-profiles';
import {
  BusyStatusReason,
  PrintingStatusReason,
} from '@shared/constants/status-reason';
import { statusTypes } from '@shared/constants/status-types';
import { IDeviceJob } from '@shared/interfaces/device/device-job';
import { IDeviceStatus } from '@shared/interfaces/device/device-status';
import { DeviceStatusService } from '@shared/modules/device/services/device-status.service';
import { StorageService } from '@shared/services/storage.service';

@Component({
  selector: 'app-device-additional-data',
  templateUrl: './device-additional-data.component.html',
  styleUrls: ['./device-additional-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceAdditionalDataComponent {
  @Input() deviceId: string;
  @Input() deviceStatus!: IDeviceStatus;
  @Input() deviceJob?: IDeviceJob;

  failsafes = failsafes;
  statusTypes = statusTypes;
  printProfileStrings = printProfileStrings;

  constructor(
    private translate: TranslateService,
    private statusService: DeviceStatusService,
    private storageService: StorageService
  ) {}

  protected isPaused(): boolean {
    return this.statusService.checkReason(
      this.deviceStatus,
      statusTypes.printing,
      [PrintingStatusReason.COMPLETED, PrintingStatusReason.PAUSED]
    );
  }

  protected isPrinting(): boolean {
    return this.deviceStatus.statusCode == statusTypes.printing;
  }

  protected get showMaterialData(): boolean {
    const isPrinting = this.statusService.checkReason(
      this.deviceStatus,
      statusTypes.printing,
      [PrintingStatusReason.HEATING, PrintingStatusReason.PREPARING_FOR_PRINT]
    );
    const isBusy = this.statusService.checkReason(
      this.deviceStatus,
      statusTypes.busy,
      [BusyStatusReason.WAITING_FOR_MATERIAL_SETUP]
    );

    return (
      isPrinting ||
      isBusy ||
      this.deviceStatus.statusCode === statusTypes.failsafe ||
      this.deviceStatus.statusCode === statusTypes.idle
    );
  }

  protected get parsingFileName(): string | null {
    return this.storageService.getSessionStorage(
      StorageService.parsingFileName(this.deviceId)
    );
  }

  protected isParsing(): boolean {
    return (
      this.statusService.checkReason(this.deviceStatus, statusTypes.printing, [
        PrintingStatusReason.PARSING,
      ]) && !!this.parsingFileName
    );
  }

  protected get GetPrintProfileImg(): string {
    return this.deviceJob?.profile == printProfiles.PRODUCTION
      ? 'assets/PrintProfileProduction.png'
      : 'assets/PrintProfileQuality.png';
  }

  checkReasonContent(): string {
    const reasonContent = this.deviceStatus.reason
      ? failsafes[this.deviceStatus.reason as keyof typeof failsafes]
      : null;

    if (reasonContent) {
      return reasonContent;
    }
    return this.translate.instant('common.undefined');
  }
}
